// URL contract of the Audit agents page (mission agent-spend-audit, spec §6.2, D-20). Pure.
//
//   /offices/:o/audit?project=&mission=&level=&lens=&sort=&model=&env=&from=&to=
//
// Invalid values are dropped (fallback to the default), never sent to the API raw.

export const AUDIT_LEVELS = Object.freeze(['project', 'mission', 'agent', 'story', 'activation', 'model']);
export const AUDIT_LENSES = Object.freeze(['cost', 'time', 'waste']);
export const AUDIT_SORT_COLUMNS = Object.freeze([
  'name', 'cost', 'tokens', 'calls', 'errorCalls', 'agentTime', 'activations', 'waste', 'wastePct', 'reruns', 'qaRetries', 'firstPass',
]);
export const LENS_COLUMN = Object.freeze({ cost: 'cost', time: 'agentTime', waste: 'waste' });
export const NONE = '_none';
export const MAX_DAYS = 93;
const DAY_MS = 86_400_000;
const ID = /^[^\s]{1,128}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function parseDay(s) {
  if (!DATE.test(s ?? '')) return null;
  const ms = Date.parse(`${s}T00:00:00.000Z`);
  if (!Number.isFinite(ms) || new Date(ms).toISOString().slice(0, 10) !== s) return null;
  return ms;
}

/** A valid [from, to) of at most 93 days, or null. */
export function validRange(from, to) {
  const a = parseDay(from);
  const b = parseDay(to);
  if (a === null || b === null || b <= a || (b - a) / DAY_MS > MAX_DAYS) return null;
  return { from, to };
}

const id = (v) => (typeof v === 'string' && ID.test(v) ? v : null);

/** The level a scope implies when none (or an inconsistent one) is asked. */
export function defaultLevel({ project, mission }) {
  if (mission) return 'agent';
  if (project) return 'mission';
  return 'project';
}

function levelFits(level, { project, mission }) {
  if (level === 'model') return true;
  if (level === 'project') return !project && !mission;
  if (level === 'mission') return Boolean(project) && !mission;
  return Boolean(mission); // agent | story | activation
}

/**
 * URLSearchParams -> the page state. Never throws.
 * @returns {{ level: string, project: string|null, mission: string|null, lens: string,
 *   sort: { col: string, dir: 'asc'|'desc' }, model: string|null, env: string|null, from: string|null, to: string|null }}
 */
export function readAuditParams(searchParams) {
  const get = (k) => {
    const v = searchParams?.get?.(k);
    return typeof v === 'string' && v.length > 0 ? v : null;
  };
  const project = id(get('project'));
  const mission = id(get('mission'));
  const scope = { project, mission };
  const askedLevel = get('level');
  const level = AUDIT_LEVELS.includes(askedLevel) && levelFits(askedLevel, scope) ? askedLevel : defaultLevel(scope);
  const lens = AUDIT_LENSES.includes(get('lens')) ? get('lens') : 'cost';
  let sort = { col: LENS_COLUMN[lens], dir: 'desc' };
  const m = /^([A-Za-z]+):(asc|desc)$/.exec(get('sort') ?? '');
  if (m && AUDIT_SORT_COLUMNS.includes(m[1])) sort = { col: m[1], dir: m[2] };
  const range = validRange(get('from'), get('to'));
  const model = id(get('model'));
  const envRaw = get('env');
  const env = envRaw && envRaw.length <= 64 && ID.test(envRaw) ? envRaw : null;
  return { level, project, mission, lens, sort, model, env, from: range?.from ?? null, to: range?.to ?? null };
}

/**
 * Page state -> URLSearchParams (defaults omitted, so the URL stays short and deep links stay canonical).
 */
export function writeAuditParams(state) {
  const qs = new URLSearchParams();
  const scope = { project: state.project ?? null, mission: state.mission ?? null };
  if (scope.project) qs.set('project', scope.project);
  if (scope.mission) qs.set('mission', scope.mission);
  if (state.level && state.level !== defaultLevel(scope) && levelFits(state.level, scope)) qs.set('level', state.level);
  const lens = AUDIT_LENSES.includes(state.lens) ? state.lens : 'cost';
  if (lens !== 'cost') qs.set('lens', lens);
  const sort = state.sort;
  if (sort && AUDIT_SORT_COLUMNS.includes(sort.col) && (sort.col !== LENS_COLUMN[lens] || sort.dir !== 'desc')) {
    qs.set('sort', `${sort.col}:${sort.dir === 'asc' ? 'asc' : 'desc'}`);
  }
  if (state.model) qs.set('model', state.model);
  if (state.env) qs.set('env', state.env);
  const range = validRange(state.from, state.to);
  if (range) { qs.set('from', range.from); qs.set('to', range.to); }
  return qs;
}

/** The current UTC month as { from, to }. */
export function currentMonth(now = Date.now()) {
  const d = new Date(now);
  const from = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  const to = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  const iso = (ms) => new Date(ms).toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

/** True when `now` falls inside the range ; a null range is the current month (live). */
export function isLive(range, now = Date.now()) {
  if (!range) return true;
  const a = parseDay(range.from);
  const b = parseDay(range.to);
  if (a === null || b === null) return false;
  return now >= a && now < b;
}

/** The API query string of a page state (level, scope, filters, period) — what the server validates again. */
export function auditApiParams(state) {
  const qs = new URLSearchParams({ level: state.level });
  if (state.project) qs.set('project', state.project);
  if (state.mission) qs.set('mission', state.mission);
  if (state.model) qs.set('model', state.model);
  if (state.env) qs.set('env', state.env);
  if (state.from && state.to) { qs.set('from', state.from); qs.set('to', state.to); }
  return qs;
}

/**
 * Where the old Consommation URL lands (S-007.AC1, spec §6.4) : the Audit page with the query string verbatim.
 * @param {string} officeId
 * @param {string} requestUrl  the full request URL of /offices/:o/settings/consumption
 */
export function consumptionRedirectTarget(officeId, requestUrl) {
  return `/offices/${officeId}/audit${new URL(requestUrl).search}`;
}
